import { getAmplitudeInstance } from '../amplitudeHelpers';
import MapBaseLayer from '../enums/MapBaseLayer';
import MapGestureAction from '../enums/MapGestureAction';
import MapLocation from '../enums/MapLocation';
import TapAnywhereType from '../enums/TapAnywhereType';

type logMapPointTappedParams = {
  custom_route_id?: number;
  is_alltrails_feature?: boolean;
  is_online?: boolean;
  is_recording?: boolean;
  lat: number;
  lng: number;
  map_base_layer?: MapBaseLayer;
  map_id?: number;
  map_location?: MapLocation;
  map_style_id?: string;
  point_type?: TapAnywhereType;
  source?: MapGestureAction;
  source_id?: string;
  tapped_layer_id?: string;
  tapped_source_id?: string;
  trail_id?: number;
  zoom_level?: number;
};

/**
 * A user taps anywhere on the map to view information about that point: distance away, elevation, routing controls, and details view. Fires on both the Explore and Navigator maps; use map_location to distinguish.
 * @param params - Attributes to log with the amplitude event
 * @param params.custom_route_id - The id of the custom route being edited, when the tap occurs on the Custom Route builder map.
 * @param params.is_alltrails_feature - Whether the tapped rendered feature is an AllTrails-owned feature (vs. a third-party/OSM style feature). Absent when the tap did not resolve to a rendered feature.
 * @param params.is_online - TRUE if the device had connectivity when the menu opened. Stats and Take Me There both require a live routing call, so both rows are absent when this is FALSE.
 * @param params.is_recording - Boolean indicator if Navigator is actively recording (true while paused).
 * @param params.lat - Latitude of the tapped map coordinate.
 * @param params.lng - Longitude of the tapped map coordinate.
 * @param params.map_base_layer - The active base map layer at the time of the tap.
 * @param params.map_id - The id of the associated map/activity, when available.
 * @param params.map_location - The map surface the tap occurred on (e.g. Explore or Navigator).
 * @param params.map_style_id - The raw style identifier (uid) of the active base layer, e.g. 'alltrailsv2-vector' or 'satellite-vector'.
 * @param params.point_type - The kind of map feature the selected point resolved to, from the highest-priority feature under the press.
 * @param params.source - The gesture that opened the menu. Both a short tap and a long press open the tap-anywhere menu.
 * @param params.source_id - The identifier of the map feature the selected point resolved to: the POI id, trail id, or waypoint id per point_type. Absent for a bare coordinate with no underlying feature.
 * @param params.tapped_layer_id - The id of the top-most rendered map layer hit by the tap (from queryRenderedFeatures). Absent when the tap landed on empty basemap with no rendered feature.
 * @param params.tapped_source_id - The Mapbox source id of the top-most rendered feature hit by the tap. Absent when the tap did not resolve to a rendered feature.
 * @param params.trail_id - The id of the associated trail, when available.
 * @param params.zoom_level - The map zoom level at the time of the tap.
 */
const logMapPointTapped = (
  {
    custom_route_id,
    is_alltrails_feature,
    is_online,
    is_recording,
    lat,
    lng,
    map_base_layer,
    map_id,
    map_location,
    map_style_id,
    point_type,
    source,
    source_id,
    tapped_layer_id,
    tapped_source_id,
    trail_id,
    zoom_level
  }: logMapPointTappedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'map point tapped',
    {
      'custom route id': custom_route_id,
      'is alltrails feature': is_alltrails_feature,
      'is online': is_online,
      'is recording': is_recording,
      lat,
      lng,
      'map base layer': map_base_layer,
      'map id': map_id,
      'map location': map_location,
      'map style id': map_style_id,
      'point type': point_type,
      source,
      'source id': source_id,
      'tapped layer id': tapped_layer_id,
      'tapped source id': tapped_source_id,
      'trail id': trail_id,
      'zoom level': zoom_level
    },
    callback
  );
};

export default logMapPointTapped;
